import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { hasConsented } from "@/lib/cookieConsent";
import { trackScrollDepth } from "@/lib/analytics";

export function ScrollDepthTracker() {
  const location = useLocation();
  const maxDepthRef = useRef(0);
  const lastFlushRef = useRef(0);
  const pathRef = useRef(location.pathname);

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    if (!hasConsented()) return;

    // Reset on path change
    maxDepthRef.current = 0;
    pathRef.current = location.pathname;

    const bucket = (pct: number) => {
      if (pct >= 100) return 100;
      if (pct >= 75) return 75;
      if (pct >= 50) return 50;
      if (pct >= 25) return 25;
      return 0;
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const scrollTop = window.scrollY;
        const docHeight = document.documentElement.scrollHeight - window.innerHeight;
        if (docHeight <= 0) { ticking = false; return; }
        const pct = Math.round((scrollTop / docHeight) * 100);
        const b = bucket(pct);
        if (b > maxDepthRef.current) {
          maxDepthRef.current = b;
        }
        ticking = false;
      });
    };

    const flush = () => {
      if (maxDepthRef.current > 0 && maxDepthRef.current !== lastFlushRef.current) {
        lastFlushRef.current = maxDepthRef.current;
        trackScrollDepth(pathRef.current, maxDepthRef.current);
      }
    };

    // Periodic flush every 10s
    const interval = setInterval(flush, 10000);

    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("beforeunload", flush);

    return () => {
      flush();
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("beforeunload", flush);
      clearInterval(interval);
    };
  }, [location.pathname]);

  return null;
}
